import { useState, useEffect } from 'react';
import { Button, Card, Form, Spinner } from 'react-bootstrap';
import { BoxArrowUpRight } from 'react-bootstrap-icons';
import { Step, StepList, StepResult, type StepStatus } from '@/components/config/StepList';
import { SystemUserSecret } from '@/components/config/SystemUserSecret';
import { ToolPage } from '@/components/ui/ToolPage';
import { nextgenBrokerService, type AccountAvailability } from '@/services/nextgenBrokerService';
import { activityService } from '@/services/activityService';
import { validateClientName, sanitizeClientName } from '@/utils/clientValidation';

export default function SetupNextgenClient() {
  const [accountId, setAccountId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientIdError, setClientIdError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameOk, setRenameOk] = useState<null | { accountId: string; name: string }>(null);
  const [renameErr, setRenameErr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [secretErr, setSecretErr] = useState<string | null>(null);
  const [gettingSecret, setGettingSecret] = useState(false);
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState<AccountAvailability | null>(null);
  const [checkErr, setCheckErr] = useState<string | null>(null);
  // Steps 2 and 3 happen on other pages. The page knows only that it sent the
  // person there, so it marks those steps as opened, never as done.
  const [configOpened, setConfigOpened] = useState(false);
  const [deployOpened, setDeployOpened] = useState(false);

  // Validate client ID whenever it changes
  useEffect(() => {
    if (clientId.trim().length > 0) {
      const error = validateClientName(clientId);
      setClientIdError(error);
    } else {
      setClientIdError(null);
    }
  }, [clientId]);

  const accountIdReady = accountId.trim().length === 12;
  const clientIdReady = !!clientId.trim() && !clientIdError;

  // Guard: two clients must never share one AWS account, and the management
  // account must never be renamed. Both happened, so the check runs on its own
  // as soon as there is something to check, and Rename stays disabled until it
  // passes. The broker enforces the same rule, so a stale page cannot get past
  // it either.
  useEffect(() => {
    setAvailability(null);
    setCheckErr(null);
    if (!accountIdReady || !clientIdReady) return;
    let cancelled = false;
    const id = setTimeout(async () => {
      setChecking(true);
      try {
        const res = await nextgenBrokerService.checkAccountAvailable(accountId.trim(), clientId.trim());
        if (!cancelled) setAvailability(res);
      } catch (e) {
        if (!cancelled) setCheckErr(e instanceof Error ? e.message : 'The check did not answer.');
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [accountId, clientId, accountIdReady, clientIdReady]);

  const canRename = accountIdReady && clientIdReady && availability?.available === true;

  const onRename = async () => {
    setRenameErr(null);
    setRenameOk(null);
    try {
      setRenaming(true);
      const res = await nextgenBrokerService.updateAccountName(accountId.trim(), clientId.trim());
      setRenameOk(res);
      // Log activity on success (non-blocking)
      activityService
        .logActivity({
          type: 'system',
          action: 'account-rename',
          resourceType: 'aws-account',
          resourceId: accountId.trim(),
          details: { metadata: { newName: clientId.trim() } },
          success: true,
        })
        .catch(() => {});
    } catch (e) {
      setRenameErr(e instanceof Error ? e.message : 'The account did not answer.');
      // Attempt to log failure (non-blocking)
      activityService
        .logActivity({
          type: 'system',
          action: 'account-rename',
          resourceType: 'aws-account',
          resourceId: accountId.trim(),
          details: { metadata: { newName: clientId.trim() } },
          success: false,
          errorMessage: e instanceof Error ? e.message : 'Unknown error',
        })
        .catch(() => {});
    } finally {
      setRenaming(false);
    }
  };

  const onGetSecret = async () => {
    setSecretErr(null);
    setSecret(null);
    try {
      setGettingSecret(true);
      // The broker reads the account and region from the client's config.
      const res = await nextgenBrokerService.getSystemUserSecret(clientId.trim());
      setSecret(res.secretString || '');
    } catch (e) {
      setSecretErr(e instanceof Error ? e.message : 'The account did not answer.');
    } finally {
      setGettingSecret(false);
    }
  };

  const openCreateConfig = () => {
    // A NextGen account, so the contract starts on Credits.
    const url = `/tools/create-client-config?clientName=${encodeURIComponent(clientId.trim())}&accountId=${encodeURIComponent(accountId.trim())}&org=nextgen`;
    window.open(url, '_blank', 'noreferrer');
    setConfigOpened(true);
  };

  const openUpdateConfig = () => {
    const url = `/tools/update-client-config?clientName=${encodeURIComponent(clientId.trim())}`;
    window.open(url, '_blank', 'noreferrer');
    setConfigOpened(true);
  };

  const openDeployments = () => {
    window.open('/deployments', '_blank', 'noreferrer');
    setDeployOpened(true);
  };

  // The current step is the first one the page has not seen done or opened.
  const passed = [!!renameOk, configOpened, deployOpened, secret !== null];
  const firstOpen = passed.indexOf(false);
  const currentStep = firstOpen === -1 ? 5 : firstOpen + 1;
  const statusOf = (step: number, verified: boolean): StepStatus => {
    if (step === currentStep) return 'current';
    if (!passed[step - 1]) return 'todo';
    return verified ? 'done' : 'started';
  };
  // Only the current step carries the primary button.
  const variantOf = (step: number) => (step === currentStep ? 'primary' : 'outline-secondary');

  const name = clientId.trim();
  const account = accountId.trim();
  const suggestion = sanitizeClientName(clientId);

  return (
    <ToolPage
      title="Set up a NextGen client"
      subtitle="Name a free pool account for the client, write its config, run the first deploy and get the system user password."
      width="narrow"
    >
      <Card className="mb-3">
        <Card.Body>
          <h2 className="section-label">The client</h2>
          <Form.Group className="mb-3" controlId="nextgen-account-id">
            <Form.Label>AWS account ID</Form.Label>
            <Form.Control
              className="mono"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="123456789012"
              inputMode="numeric"
            />
            <Form.Text>The 12-digit ID of a free NextGen pool account.</Form.Text>
          </Form.Group>
          <Form.Group controlId="nextgen-client-name">
            <Form.Label>Client name</Form.Label>
            <Form.Control
              className="mono"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="arcanum-demo"
              isInvalid={!!clientIdError}
            />
            {clientIdError ? (
              <>
                <Form.Control.Feedback type="invalid">{clientIdError}</Form.Control.Feedback>
                {suggestion && suggestion !== clientId && (
                  <Form.Text>
                    Try <span className="mono">{suggestion}</span>.
                  </Form.Text>
                )}
              </>
            ) : (
              <Form.Text>
                Lowercase letters, numbers and dashes. It becomes the AWS account's name, the client's name in this
                portal and its web address.
              </Form.Text>
            )}
          </Form.Group>

          {/* The account check runs before anything is renamed or deployed. */}
          {checking && <StepResult tone="busy">Checking that account {account} is free.</StepResult>}
          {!checking && checkErr && (
            <StepResult tone="warning">
              Could not check the account: {checkErr} The rename stays off until the check passes.
            </StepResult>
          )}
          {!checking && !checkErr && availability && !availability.available && (
            <StepResult tone="danger">
              <strong>Do not use this account.</strong> {availability.reason}
            </StepResult>
          )}
          {!checking && !checkErr && availability?.available && (
            <StepResult tone="success">
              Account <span className="mono">{availability.accountId}</span>
              {availability.accountName ? ` (${availability.accountName})` : ''} is a free pool account with nothing
              deployed in it.
            </StepResult>
          )}
          {!checking && !checkErr && !availability && (!accountIdReady || !clientIdReady) && (
            <StepResult tone="muted">
              Enter a 12-digit account ID and a client name. The account is checked before you can rename it.
            </StepResult>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <StepList>
            <Step
              number={1}
              title="Name the account"
              status={statusOf(1, true)}
              description={
                <>
                  Renames AWS account <span className="mono">{account || 'the account'}</span> to{' '}
                  <span className="mono">{name || 'the client name'}</span>. Nothing else in the account changes, and
                  nothing is deployed.
                </>
              }
            >
              <div className="step-list__actions">
                <Button variant={variantOf(1)} onClick={onRename} disabled={!canRename || renaming}>
                  {renaming ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Renaming
                    </>
                  ) : (
                    'Rename account'
                  )}
                </Button>
              </div>
              {renameOk && (
                <StepResult tone="success">
                  Account <span className="mono">{renameOk.accountId}</span> is now named{' '}
                  <span className="mono">{renameOk.name}</span>.
                </StepResult>
              )}
              {renameErr && (
                <StepResult tone="danger">
                  <strong>The account was not renamed.</strong> {renameErr}
                </StepResult>
              )}
            </Step>

            <Step
              number={2}
              title="Write the config"
              status={statusOf(2, false)}
              description="Opens the config form in a new tab with the client name and the account filled in. Use Update if the client already has a config."
            >
              <div className="step-list__actions">
                <Button variant={variantOf(2)} onClick={openCreateConfig}>
                  Create config
                  <BoxArrowUpRight className="ms-2" aria-hidden="true" />
                </Button>
                <Button variant="outline-secondary" onClick={openUpdateConfig}>
                  Update config
                  <BoxArrowUpRight className="ms-2" aria-hidden="true" />
                </Button>
              </div>
              {configOpened && (
                <StepResult tone="muted">Opened in a new tab. Come back here when the config is saved.</StepResult>
              )}
            </Step>

            <Step
              number={3}
              title="Run the first deploy"
              status={statusOf(3, false)}
              description={
                <>
                  Opens Deployments in a new tab. Start a deployment for{' '}
                  <span className="mono">{name || 'the client'}</span> there and watch its log until it finishes.
                </>
              }
            >
              <div className="step-list__actions">
                <Button variant={variantOf(3)} onClick={openDeployments}>
                  Open Deployments
                  <BoxArrowUpRight className="ms-2" aria-hidden="true" />
                </Button>
              </div>
              {deployOpened && (
                <StepResult tone="muted">Opened in a new tab. Come back here when the deploy has finished.</StepResult>
              )}
            </Step>

            <Step
              number={4}
              title="Get the system user password"
              status={statusOf(4, true)}
              description="Reads the sign-in the deploy made for the system user. It exists only after the first deploy finishes. Nothing in the account changes."
            >
              <div className="step-list__actions">
                <Button variant={variantOf(4)} onClick={onGetSecret} disabled={!clientIdReady || gettingSecret}>
                  {gettingSecret ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Getting the password
                    </>
                  ) : (
                    'Get password'
                  )}
                </Button>
              </div>
              {secret !== null && (
                <div className="mt-3">
                  <SystemUserSecret raw={secret} />
                </div>
              )}
              {secretErr && (
                <StepResult tone="danger">
                  <strong>Could not get the password.</strong> {secretErr} Check that the first deploy has finished.
                </StepResult>
              )}
            </Step>

            <Step
              number={5}
              title="Open the workspace"
              status={statusOf(5, false)}
              description="The client's Numa address, made from the client name. It works once the first deploy has finished. Sign in as the system user."
            >
              {name ? (
                <a
                  href={`https://${name}.numa.arcanum.ai`}
                  target="_blank"
                  rel="noreferrer"
                  className="step-list__link"
                >
                  https://{name}.numa.arcanum.ai
                </a>
              ) : (
                <span className="tool-form__hint">Enter a client name above to get the address.</span>
              )}
            </Step>
          </StepList>
        </Card.Body>
      </Card>
    </ToolPage>
  );
}
